import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, ListBullets, Palette, Signpost, Trash } from "@phosphor-icons/react";
import { useTheme, type Theme } from "../lib/useTheme.tsx";

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: "dragons-hoard-light", label: "Dragon's Hoard · Light" },
  { value: "dragons-hoard", label: "Dragon's Hoard · Dark" },
  { value: "talktok", label: "TalkTok" },
];

/** Session/account overflow menu, ported from the redesign mockup's `.menu`
 *  block. Opens off the gear button in the V2 topbar. */
export function GearMenu({
  sessionId,
  onClearContent,
  onClose,
}: {
  sessionId: string | null;
  onClearContent: () => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      // Ignore the gear trigger itself: closing on its mousedown would make
      // the button's click immediately reopen the menu (toggle can't close).
      if (target instanceof Element && target.closest("[data-gear-menu-trigger]")) return;
      if (ref.current && target && !ref.current.contains(target)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  function copySessionId() {
    if (sessionId) void navigator.clipboard?.writeText(sessionId).catch(() => {});
    onClose();
  }

  return (
    <div className="menu admin2-menu" data-testid="admin2-gear-menu" role="menu" ref={ref}>
      <label className="menu-item menu-theme">
        <Palette /> Theme
        <select
          className="menu-theme-select"
          data-testid="admin2-theme-select"
          value={theme}
          onChange={(event) => setTheme(event.target.value as Theme)}
        >
          {THEME_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <div className="menu-sep" />
      <button
        type="button"
        className="menu-item"
        role="menuitem"
        data-testid="admin2-copy-session-id"
        disabled={!sessionId}
        onClick={copySessionId}
      >
        <Copy /> Copy session ID
      </button>
      <button
        type="button"
        className="menu-item danger"
        role="menuitem"
        data-testid="admin2-clear-session-content"
        disabled={!sessionId}
        onClick={() => {
          onClose();
          onClearContent();
        }}
      >
        <Trash /> Clear session content…
      </button>
      <div className="menu-sep" />
      <button
        type="button"
        className="menu-item"
        role="menuitem"
        onClick={() => {
          onClose();
          navigate("/admin/sessions");
        }}
      >
        <ListBullets /> All sessions
      </button>
      <button
        type="button"
        className="menu-item"
        role="menuitem"
        onClick={() => {
          onClose();
          navigate("/admin/llm-settings");
        }}
      >
        <Signpost /> LLM Settings
      </button>
    </div>
  );
}
